'use client';

import Link from 'next/link';
import { useEffect, useId, useRef, useState } from 'react';
import { ComposeLegalFooter } from '@/platform/legal/ComposeLegalFooter';
import { forumCaptionTitle, type ForumComposeKind } from '@/platform/forum/composeReady';
import { ForumComposeMediaFields } from '@/platform/forum/ForumComposeMediaFields';
import { FORUM_IMAGE_ACCEPT, MAX_FORUM_IMAGES } from '@/platform/forum/forumUploads';

export type { ForumComposeKind };
export type FeedComposeKind = ForumComposeKind;

type Props = {
  avatarLetter: string;
  title: string;
  body: string;
  onTitleChange: (value: string) => void;
  onBodyChange: (value: string) => void;
  youtubeUrl: string;
  onYoutubeUrl: (value: string) => void;
  imagePreviews: string[];
  uploading: boolean;
  imageCount: number;
  onAddFiles: (files: FileList | null) => void;
  canPost: boolean;
  onPost: () => void | Promise<void>;
  error?: string;
  showLegal?: boolean;
  showBlogLink?: boolean;
  youtubeInputId?: string;
  bodyPlaceholder?: string;
  /** Remount/collapse after a successful post (parent increments). */
  collapseKey?: number;
  /** Notify parent so canPost / payload can scope to this form. */
  onKindChange?: (kind: ForumComposeKind | null) => void;
  /** Clear media that does not belong on the selected form. */
  onPrepareKind?: (kind: ForumComposeKind) => void;
};

/**
 * LinkedIn-style share box with three separate compose forms:
 * post (title + body), video (YouTube + caption), photo (uploads + caption).
 */
export function FeedComposeAccordion({
  avatarLetter,
  title,
  body,
  onTitleChange,
  onBodyChange,
  youtubeUrl,
  onYoutubeUrl,
  imagePreviews,
  uploading,
  imageCount,
  onAddFiles,
  canPost,
  onPost,
  error,
  showLegal = true,
  showBlogLink = true,
  youtubeInputId,
  bodyPlaceholder = 'What do you want to discuss?',
  collapseKey = 0,
  onKindChange,
  onPrepareKind,
}: Props) {
  const startId = useId();
  const photoPickerRef = useRef<HTMLInputElement>(null);
  const photoCaptureRef = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<ForumComposeKind | null>(null);
  const [photoChooserOpen, setPhotoChooserOpen] = useState(false);

  useEffect(() => {
    setKind(null);
    setPhotoChooserOpen(false);
    onKindChange?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- parent notify on collapse key only
  }, [collapseKey]);

  const open = kind !== null;
  const photoPickDisabled = uploading || imageCount >= MAX_FORUM_IMAGES;

  function setComposeKind(next: ForumComposeKind | null) {
    setKind(next);
    onKindChange?.(next);
  }

  function expand(next: ForumComposeKind) {
    setPhotoChooserOpen(false);
    onPrepareKind?.(next);
    if (next === 'video') {
      onTitleChange(forumCaptionTitle(body, 'Video'));
    } else if (next === 'photo') {
      onTitleChange(forumCaptionTitle(body, 'Photos'));
    }
    setComposeKind(next);
  }

  function collapse() {
    setPhotoChooserOpen(false);
    setComposeKind(null);
  }

  function onCaptionChange(value: string, fallback: string) {
    onBodyChange(value);
    onTitleChange(forumCaptionTitle(value, fallback));
  }

  function onPhotoFiles(files: FileList | null) {
    setPhotoChooserOpen(false);
    onPrepareKind?.('photo');
    onTitleChange(forumCaptionTitle(body, 'Photos'));
    setComposeKind('photo');
    onAddFiles(files);
  }

  function pickFromGallery() {
    onPrepareKind?.('photo');
    onTitleChange(forumCaptionTitle(body, 'Photos'));
    setComposeKind('photo');
    setPhotoChooserOpen(false);
    photoPickerRef.current?.click();
  }

  function takePhoto() {
    onPrepareKind?.('photo');
    onTitleChange(forumCaptionTitle(body, 'Photos'));
    setComposeKind('photo');
    setPhotoChooserOpen(false);
    photoCaptureRef.current?.click();
  }

  function formActions() {
    return (
      <div className="feed-compose-actions">
        <button type="button" className="btn btn-secondary" onClick={collapse}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" onClick={() => void onPost()} disabled={!canPost}>
          Post
        </button>
      </div>
    );
  }

  function formHead(label: string) {
    return (
      <div className="feed-card__compose-head">
        <div className="profile-photo feed-card__avatar" aria-hidden>
          {avatarLetter}
        </div>
        <span className="text-muted">{label}</span>
      </div>
    );
  }

  return (
    <article
      className={`feed-card feed-card--compose${open ? ' feed-card--compose-open' : ' feed-card--compose-collapsed'}`}
      data-testid="feed-compose"
      data-compose-open={open ? 'true' : 'false'}
      data-compose-kind={kind ?? ''}
    >
      <input
        ref={photoPickerRef}
        className="forum-compose-media__file-input"
        type="file"
        accept={FORUM_IMAGE_ACCEPT}
        multiple
        disabled={photoPickDisabled}
        data-testid="feed-compose-photo-gallery"
        tabIndex={-1}
        onChange={(e) => {
          onPhotoFiles(e.target.files);
          e.target.value = '';
        }}
      />
      <input
        ref={photoCaptureRef}
        className="forum-compose-media__file-input"
        type="file"
        accept={FORUM_IMAGE_ACCEPT}
        capture="environment"
        disabled={photoPickDisabled}
        data-testid="feed-compose-photo-capture"
        tabIndex={-1}
        onChange={(e) => {
          onPhotoFiles(e.target.files);
          e.target.value = '';
        }}
      />

      {!open ? (
        <>
          <div className="feed-compose-launcher">
            <div className="profile-photo feed-card__avatar" aria-hidden>
              {avatarLetter}
            </div>
            <button
              type="button"
              id={startId}
              className="feed-compose-launcher__start"
              data-testid="feed-compose-start"
              onClick={() => expand('post')}
            >
              Start a post
            </button>
          </div>
          <div className="feed-compose-types" role="group" aria-label="Create post type">
            <button
              type="button"
              className="feed-compose-types__btn"
              data-testid="feed-compose-video"
              onClick={() => expand('video')}
            >
              <i className="uil uil-video" aria-hidden />
              <span>Video</span>
            </button>
            <button
              type="button"
              className="feed-compose-types__btn"
              data-testid="feed-compose-photo"
              aria-expanded={photoChooserOpen}
              aria-controls={photoChooserOpen ? 'feed-compose-photo-chooser' : undefined}
              onClick={() => setPhotoChooserOpen(true)}
            >
              <i className="uil uil-image" aria-hidden />
              <span>Photo</span>
            </button>
            {showBlogLink ? (
              <Link href="/blog/compose/" className="feed-compose-types__btn" data-testid="feed-compose-article">
                <i className="uil uil-file-alt" aria-hidden />
                <span>Write article</span>
              </Link>
            ) : null}
          </div>
          {photoChooserOpen ? (
            <div
              id="feed-compose-photo-chooser"
              className="feed-compose-photo-chooser"
              role="group"
              aria-label="Add photos"
              data-testid="feed-compose-photo-chooser"
            >
              <button
                type="button"
                className="btn btn-secondary"
                data-testid="feed-compose-photo-add"
                disabled={photoPickDisabled}
                onClick={pickFromGallery}
              >
                Add photos
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                data-testid="feed-compose-photo-take"
                disabled={photoPickDisabled}
                onClick={takePhoto}
              >
                Take photo
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setPhotoChooserOpen(false)}>
                Cancel
              </button>
            </div>
          ) : null}
        </>
      ) : kind === 'video' ? (
        <>
          {formHead('Share a video')}
          <ForumComposeMediaFields
            mode="video"
            youtubeUrl={youtubeUrl}
            onYoutubeUrl={onYoutubeUrl}
            imagePreviews={[]}
            uploading={false}
            imageCount={0}
            onAddFiles={() => undefined}
            youtubeInputId={youtubeInputId}
          />
          <textarea
            className="feed-card__compose-input"
            value={body}
            onChange={(e) => onCaptionChange(e.target.value, 'Video')}
            placeholder="Write a caption…"
            rows={3}
            data-testid="feed-compose-caption"
          />
          {formActions()}
          {error ? <p className="form-error">{error}</p> : null}
          {showLegal ? <ComposeLegalFooter /> : null}
        </>
      ) : kind === 'photo' ? (
        <>
          {formHead('Share photos')}
          <ForumComposeMediaFields
            mode="photo"
            youtubeUrl=""
            onYoutubeUrl={() => undefined}
            imagePreviews={imagePreviews}
            uploading={uploading}
            imageCount={imageCount}
            onAddFiles={onAddFiles}
          />
          <textarea
            className="feed-card__compose-input"
            value={body}
            onChange={(e) => onCaptionChange(e.target.value, 'Photos')}
            placeholder="Write a caption…"
            rows={3}
            data-testid="feed-compose-caption"
          />
          {formActions()}
          {error ? <p className="form-error">{error}</p> : null}
          {showLegal ? <ComposeLegalFooter /> : null}
        </>
      ) : (
        <>
          {formHead('Start a post')}
          <input
            className="feed-card__compose-input"
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            placeholder="Title"
            autoFocus
          />
          <textarea
            className="feed-card__compose-input"
            value={body}
            onChange={(e) => onBodyChange(e.target.value)}
            placeholder={bodyPlaceholder}
            rows={3}
          />
          {formActions()}
          {error ? <p className="form-error">{error}</p> : null}
          {showLegal ? <ComposeLegalFooter /> : null}
        </>
      )}
    </article>
  );
}
